import * as React from 'react'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { Form, Box } from 'cs-kevlar-react'
import { Formik } from 'formik'
import { EditModalActionBar } from '../EditModalActionBar'
import { TablePicker } from '../TablePicker'
import { EditUserForm, EditUserFormSchema } from './EditUserForm'
import { User, EditableUser, CustomerAdmin } from '../../contexts/base'
import { determineDefaultPhone, determineDefaultEmail } from 'utils/users'

interface AddUserProps {
  onClose: () => void
  availableUsers: User[]
  activeAdminUsers: CustomerAdmin[]
  onAdd: (values: EditableUser) => void
  canViewPermissions: boolean
  canViewCapabilities: boolean
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingBottom: theme.spacing(8),
    },
    gridItem: {
      width: '100%',
    },
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
  }),
)

const userSearchColumns = [
  {
    label: 'Name',
    sortable: false,
    sortByDefault: false,
    filterable: true,
    dataProperty: 'fullName' as keyof EditableUser,
  },
  {
    label: 'Username',
    sortable: false,
    sortByDefault: false,
    filterable: true,
    dataProperty: 'name' as keyof EditableUser,
  },
]

export const AddUser: React.FunctionComponent<AddUserProps> = ({
  onClose,
  availableUsers,
  activeAdminUsers,
  onAdd,
  canViewPermissions,
  canViewCapabilities,
}) => {
  const classes = useStyles({})

  const [user, setUser] = React.useState<EditableUser | null>(null)

  const onSelectUser = (id: string) => {
    var adminUser = {} as CustomerAdmin
    const matchedAdminUsers = activeAdminUsers.filter(adminUser => adminUser.userId === id)
    if (matchedAdminUsers.length > 0) adminUser = matchedAdminUsers[0]
    
    const matchedUsers = availableUsers.filter(user => user.id === id)
    if (matchedUsers.length === 0) return

    const matchedUser = matchedUsers[0]

    var selectedUser: EditableUser = {
      id: matchedUser.id,
      fullName: matchedUser.fullName,
      name: matchedUser.name,
      phone: adminUser.phone ? adminUser.phone : determineDefaultPhone(matchedUser),
      externalPhoneList: matchedUser.phones?.map(x => x.number) ?? [],
      email: adminUser.email ? adminUser.email : determineDefaultEmail(matchedUser),
      externalEmailList: matchedUser.emailAddresses?.map(x => x.email) ?? [],
      capabilities: matchedUser.capabilities,
      permissions: matchedUser.permissions,
      globalConfiguration: matchedUser.globalConfiguration,
      userAgencyGlobalConfiguration: matchedUser.userAgencyGlobalConfiguration,
    }

    setUser(selectedUser)
  }

  const onSearch = () => {
    setUser(null)
  }

  return (
    <React.Fragment>
      {user === null && (
        <Box className={classes.root}>
          <TablePicker
            availableItems={availableUsers}
            onSelect={onSelectUser}
            searchLabel="Search"
            columns={userSearchColumns}
            uniqueIdentifier="AddUserPicker"
            singularAdjective="user"
            pluralAdjective="users"
            ariaLabel="Select User"
          />
        </Box>
      )}
      {user !== null && (
        <Formik
          initialValues={user}
          validationSchema={EditUserFormSchema}
          onSubmit={onAdd}
        >
          {formikProps => (
            <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
              <Box className={classes.formFieldContainer}>
                <EditUserForm
                  formikProps={formikProps}
                  allowSearch={true}
                  onSearch={onSearch}
                  canViewPermissions={canViewPermissions}
                  canViewCapabilities={canViewCapabilities}
                  canViewGlobalConfiguration={false}
                />
              </Box>
              <Box className={classes.spacer} />
              <EditModalActionBar
                canDelete={false}
                onDelete={() => {}}
                onCancel={onClose}
                formikProps={formikProps}
                saveText="Add"
              />
            </Form>
          )}
        </Formik>
      )}
    </React.Fragment>
  )
}
